import React, { useState } from 'react';
import '../../../shared/StyledInput.css';
import RequiredMark from '../../../shared/RequiredMark';
import CancelButton from '../../../shared/CancelButton';
import SaveButton from '../../../shared/SaveButton';
import NumberInput from '../../../shared/NumberInput';
import DateInput from '../../../shared/DateInput';
import TaxBasisLine from '../../../shared/TaxBasisLine';

const SIZES = [
  { key: 'xs', label: 'Extra Small', step: '250 g' },
  { key: 's', label: 'Small', step: '500 g' },
  { key: 'm', label: 'Medium', step: '1 kg' },
  { key: 'l', label: 'Large', step: '3 kg' },
  { key: 'xl', label: 'Extra Large', step: '5 kg' },
];
const SERVICES = [
  { key: 'parcel_post', label: 'Parcel Post' },
  { key: 'express_post', label: 'Express Post' },
];
const ZONES = ['1', '2', '3'];
const BANDS = [1, 2, 3, 4, 5];
// Australia Post's parcel zones for over 5 kg (backend
// config/australiaPostParcelZones.js): lodged in each row, to each column.
const LODGEMENT_ZONES = [
  ['N1', 'Sydney'], ['N2', 'Other New South Wales'], ['V1', 'Melbourne'], ['V2', 'Other Victoria'],
  ['Q1', 'Brisbane'], ['Q2', 'Near Queensland'], ['Q3', 'Middle Queensland'], ['Q4', 'North Queensland'],
  ['S1', 'Adelaide'], ['S2', 'Other South Australia'], ['NT1', 'Northern Territory'], ['W1', 'Perth'],
  ['W2', 'Middle Western Australia'], ['W3', 'North Western Australia'], ['T1', 'Tasmania'], ['NF', 'Norfolk Island'],
];
const DESTINATION_ZONES = [
  ...LODGEMENT_ZONES,
  ['W4', 'Christmas Island and Cocos (Keeling) Islands'], ['AAT', 'Australian Antarctic Territories'],
];

const sectionHeaderStyle = { margin: '0 0 10px', fontSize: 12, fontWeight: 600, color: '#8a94a6', textTransform: 'uppercase', letterSpacing: 0.6 };
const sectionStyle = { marginTop: 14, padding: 16, background: '#fff', borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', maxWidth: 640 };
const th = { padding: '4px 6px', fontSize: 12, fontWeight: 600, color: '#555', textAlign: 'left' };
const td = { padding: '3px 6px' };
// Optional features' labels, the same size as the tables' row labels.
const rowLabel = { fontSize: 13 };
const HELP = { fontSize: 12, color: '#888', marginTop: 6 };
// Underlined Tabs (ui-architecture.md).
const TABS_ROW_STYLE = { display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap', borderBottom: '1px solid #b3bbc7', marginBottom: 10 };
const TAB_WRAP_STYLE = { display: 'flex', alignItems: 'center', gap: 2, paddingBottom: 10 };
function tabColors(selected) {
  return {
    fontWeight: selected ? 700 : 400,
    color: selected ? '#222' : '#666',
    borderBottom: selected ? '2px solid #000' : '2px solid transparent',
  };
}
const tabButton = { background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 13, color: 'inherit', fontWeight: 'inherit' };

const str = (v) => (v === null || v === undefined || v === '' ? '' : String(Number(v)));
const bandList = (list) => [0, ...BANDS].map((b) => (b === 0 ? '0' : str(Array.isArray(list) ? list[b] : '')));

function draftFrom(card) {
  return {
    effective_from: card?.effective_from || '',
    name: card?.name || '',
    standard_rates: Object.fromEntries(SERVICES.map((s) => [s.key, Object.fromEntries(SIZES.map((z) => [z.key, str(card?.standard_rates?.[s.key]?.[z.key])]))])),
    savings_up_to_5kg: Object.fromEntries(ZONES.map((zone) => [zone, bandList(card?.savings_up_to_5kg?.[zone])])),
    savings_over_5kg: bandList(card?.savings_over_5kg),
    extra_cover_charge: str(card?.extra_cover_charge),
    extra_cover_savings: bandList(card?.extra_cover_savings),
    signature_charge: str(card?.signature_charge),
    per_kg_over_5kg: Object.fromEntries(SERVICES.map((s) => [s.key, Object.fromEntries(LODGEMENT_ZONES.map(([from]) => [
      from, Object.fromEntries(DESTINATION_ZONES.map(([to]) => [to, str(card?.per_kg_over_5kg?.[s.key]?.[from]?.[to])])),
    ]))])),
  };
}

// Add/Edit one MyPost Business Rate Card (issue #328), as printed in
// Australia Post's MyPost Business Rates and Savings Guide: the standard
// rate for each size of Australia Post packaging (and own packaging up to
// that size's weight step, issue #329), the % saving for each band, Extra
// Cover and Signature on Delivery, and own packaging's per-kg rate over
// 5 kg from the Post Charges Guide (issue #330). Prices inc GST, as the
// guides have them. `initial` fills it (an Add starts from the newest card, so a
// new guide only needs what changed); `isNew` clears the Effective From.
function RateCardForm({ initial, isNew, onSave, onCancel, loading }) {
  const [draft, setDraft] = useState(() => ({ ...draftFrom(initial), ...(isNew ? { effective_from: '', name: '' } : {}) }));
  const [error, setError] = useState('');
  const [perKgService, setPerKgService] = useState(SERVICES[0].key);

  const setRate = (service, size, value) => setDraft((d) => ({ ...d, standard_rates: { ...d.standard_rates, [service]: { ...d.standard_rates[service], [size]: value } } }));
  const setZoneSaving = (zone, band, value) => setDraft((d) => {
    const list = [...d.savings_up_to_5kg[zone]];
    list[band] = value;
    return { ...d, savings_up_to_5kg: { ...d.savings_up_to_5kg, [zone]: list } };
  });
  const setPerKg = (service, from, to, value) => setDraft((d) => ({
    ...d,
    per_kg_over_5kg: {
      ...d.per_kg_over_5kg,
      [service]: { ...d.per_kg_over_5kg[service], [from]: { ...d.per_kg_over_5kg[service][from], [to]: value } },
    },
  }));
  const setBandSaving = (field, band, value) => setDraft((d) => {
    const list = [...d[field]];
    list[band] = value;
    return { ...d, [field]: list };
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!draft.effective_from) return setError('Effective From is required.');
    setError('');
    return onSave(draft);
  };

  const percentCell = (value, onChange, title) => (
    <NumberInput maxDecimals={2} min={0} max={100} value={value} onChange={(e) => onChange(e.target.value)} title={title} style={{ width: 70 }} />
  );

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ ...sectionStyle, marginTop: 0 }}>
        <h4 style={sectionHeaderStyle}>Rate Card</h4>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <label>Effective From<RequiredMark /></label><br />
            <DateInput value={draft.effective_from} onChange={(e) => setDraft((d) => ({ ...d, effective_from: e.target.value }))} style={{ width: 180 }} />
          </div>
          <div style={{ flex: 1, minWidth: 240 }}>
            <label>Name</label><br />
            <input className="styled-input" value={draft.name} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} maxLength={150} placeholder="e.g., MyPost Business Rates and Savings Guide, July 2026" style={{ width: '100%' }} />
          </div>
        </div>
        <div style={HELP}>Priced from this date until the next Rate Card’s Effective From. Enter a new one ahead of an Australia Post price rise.</div>
      </div>

      <div style={sectionStyle}>
        <h4 style={sectionHeaderStyle}>Standard rates</h4>
        <table style={{ borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}>Size</th>
              <th style={th}>Own packaging</th>
              {SERVICES.map((s) => <th key={s.key} style={th}>{s.label}<TaxBasisLine>inc GST</TaxBasisLine></th>)}
            </tr>
          </thead>
          <tbody>
            {SIZES.map((z) => (
              <tr key={z.key}>
                <td style={{ ...td, fontSize: 13 }}>{z.label}</td>
                <td style={{ ...td, fontSize: 13 }}>Up to {z.step}</td>
                {SERVICES.map((s) => (
                  <td key={s.key} style={td}>
                    <NumberInput decimals={2} min={0} value={draft.standard_rates[s.key][z.key]} onChange={(e) => setRate(s.key, z.key, e.target.value)} title={`${s.label} ${z.label}`} style={{ width: 110 }} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <div style={HELP}>A satchel or box is priced at its size’s rate, up to 5 kg. Own packaging up to 5 kg is priced at the first row its weight fits: the greater of its actual weight and cubic weight (250 kg per cubic metre).</div>
      </div>

      <div style={sectionStyle}>
        <h4 style={sectionHeaderStyle}>% saving off the standard rate</h4>
        <table style={{ borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th} />
              {BANDS.map((b) => <th key={b} style={th}>Band {b}</th>)}
            </tr>
          </thead>
          <tbody>
            {ZONES.map((zone) => (
              <tr key={zone}>
                <td style={{ ...td, fontSize: 13, whiteSpace: 'nowrap' }}>Up to 5 kg, Zone {zone}</td>
                {BANDS.map((b) => (
                  <td key={b} style={td}>{percentCell(draft.savings_up_to_5kg[zone][b], (v) => setZoneSaving(zone, b, v), `Zone ${zone}, Band ${b} (%)`)}</td>
                ))}
              </tr>
            ))}
            <tr>
              <td style={{ ...td, fontSize: 13, whiteSpace: 'nowrap' }}>Over 5 kg, any zone</td>
              {BANDS.map((b) => (
                <td key={b} style={td}>{percentCell(draft.savings_over_5kg[b], (v) => setBandSaving('savings_over_5kg', b, v), `Over 5 kg, Band ${b} (%)`)}</td>
              ))}
            </tr>
          </tbody>
        </table>
        <div style={HELP}>Band 0 is the standard rate, no saving. Zone 1: same city or area; Zone 2: to another capital or major area; Zone 3: to rural.</div>
      </div>

      <div style={{ ...sectionStyle, maxWidth: 'none' }}>
        <h4 style={sectionHeaderStyle}>Own packaging over 5 kg: per kg</h4>
        <div style={TABS_ROW_STYLE}>
          {SERVICES.map((s) => (
            <div key={s.key} style={{ ...TAB_WRAP_STYLE, ...tabColors(perKgService === s.key) }}>
              <button type="button" style={tabButton} onClick={() => setPerKgService(s.key)}>{s.label}</button>
            </div>
          ))}
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ ...th, whiteSpace: 'nowrap' }}>From ↓ To →</th>
                {DESTINATION_ZONES.map(([to, name]) => <th key={to} style={th} title={name}>{to}</th>)}
              </tr>
            </thead>
            <tbody>
              {LODGEMENT_ZONES.map(([from, name]) => (
                <tr key={from}>
                  <td style={{ ...td, fontSize: 13, whiteSpace: 'nowrap' }} title={name}>{from} {name}</td>
                  {DESTINATION_ZONES.map(([to, toName]) => (
                    <td key={to} style={td}>
                      <NumberInput decimals={2} min={0} value={draft.per_kg_over_5kg[perKgService][from][to]} onChange={(e) => setPerKg(perKgService, from, to, e.target.value)} title={`${name} to ${toName}, per kg`} style={{ width: 64 }} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={HELP}>Inc GST, from the Post Charges Guide. Own packaging over 5 kg up to 22 kg is the Extra Large rate (5 kg included) plus this rate for each kg or part over 5 kg, from the business’s parcel zone to the delivery’s, less the over-5 kg saving.</div>
      </div>

      <div style={sectionStyle}>
        <h4 style={sectionHeaderStyle}>Optional features</h4>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <label style={rowLabel}>Extra Cover, per $100 above the first $100 (inc GST)</label><br />
            <NumberInput decimals={2} min={0} value={draft.extra_cover_charge} onChange={(e) => setDraft((d) => ({ ...d, extra_cover_charge: e.target.value }))} style={{ width: 110 }} />
          </div>
          <div>
            <label style={rowLabel}>Signature on Delivery (inc GST)</label><br />
            <NumberInput decimals={2} min={0} value={draft.signature_charge} onChange={(e) => setDraft((d) => ({ ...d, signature_charge: e.target.value }))} style={{ width: 110 }} />
          </div>
        </div>
        <table style={{ borderCollapse: 'collapse', marginTop: 10 }}>
          <thead>
            <tr>
              <th style={th} />
              {BANDS.map((b) => <th key={b} style={th}>Band {b}</th>)}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ ...td, fontSize: 13, whiteSpace: 'nowrap' }}>Extra Cover % saving</td>
              {BANDS.map((b) => (
                <td key={b} style={td}>{percentCell(draft.extra_cover_savings[b], (v) => setBandSaving('extra_cover_savings', b, v), `Extra Cover, Band ${b} (%)`)}</td>
              ))}
            </tr>
          </tbody>
        </table>
        <div style={HELP}>Signature on Delivery has no saving.</div>
      </div>

      {error && <div style={{ color: 'red', marginTop: 8 }}>{error}</div>}

      <div style={{ marginTop: 12 }}>
        <SaveButton loading={loading} />
        <CancelButton onClick={onCancel} disabled={loading} style={{ marginLeft: 8 }} />
      </div>
    </form>
  );
}

export default RateCardForm;
